import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';

// Resolves opaque Places API photoReferences to renderable image URLs via the
// getPlacePhoto gateway function, so the API key never reaches the client.
// Resolved object URLs are cached per session (module-level, per browser tab —
// they can't survive a reload anyway) so repeat views of the same photo don't
// re-invoke the function.

interface PlacePhotoResponse {
  contentType: string;
  base64: string;
}

const objectUrlCache = new Map<string, string>();
const inflightRequests = new Map<string, Promise<string>>();

function cacheKeyFor(photoReference: string, maxWidth?: number, maxHeight?: number): string {
  return `${photoReference}_${maxWidth ?? ''}x${maxHeight ?? ''}`;
}

function base64ToObjectUrl(base64: string, contentType: string): string {
  const byteChars = atob(base64);
  const byteNumbers = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([byteNumbers], { type: contentType });
  return URL.createObjectURL(blob);
}

/** Synchronous cache-only lookup — never triggers a network call. */
export function getCachedPlacePhotoUrl(
  photoReference: string | undefined,
  options?: { maxWidth?: number; maxHeight?: number }
): string | undefined {
  if (!photoReference) {
    return undefined;
  }
  return objectUrlCache.get(cacheKeyFor(photoReference, options?.maxWidth, options?.maxHeight));
}

export async function resolvePlacePhotoUrl(
  photoReference: string,
  options?: { maxWidth?: number; maxHeight?: number }
): Promise<string> {
  const cacheKey = cacheKeyFor(photoReference, options?.maxWidth, options?.maxHeight);

  const cached = objectUrlCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const inflight = inflightRequests.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    const getPlacePhotoCallable = httpsCallable<
      { photoReference: string; maxWidth?: number; maxHeight?: number },
      PlacePhotoResponse
    >(functions, 'getPlacePhoto');

    const { data } = await getPlacePhotoCallable({
      photoReference,
      maxWidth: options?.maxWidth,
      maxHeight: options?.maxHeight,
    });

    const url = base64ToObjectUrl(data.base64, data.contentType);
    objectUrlCache.set(cacheKey, url);
    return url;
  })();

  inflightRequests.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}

/** Evicts cached URLs for the given references so the next resolve re-fetches. */
export function invalidateCachedPlacePhotos(photoReferences: string[] | undefined): void {
  if (!photoReferences?.length) {
    return;
  }
  for (const key of Array.from(objectUrlCache.keys())) {
    if (photoReferences.some((ref) => key.startsWith(`${ref}_`))) {
      objectUrlCache.delete(key);
    }
  }
}
